import type { GameState } from "./types";
import {
  activeVictoryCondition,
  countOwnedCities,
  countPlayerSupplyTrucks,
  ownsAllMonuments,
} from "./victory";

function enemyCompletedObjective(
  source: Pick<GameState, "victoryConditions" | "captureCityTarget" | "tiles">,
): boolean {
  const condition = activeVictoryCondition(source);
  if (condition === "capture") {
    const target = source.captureCityTarget ?? 0;
    return target > 0 && countOwnedCities(source.tiles, "p2") >= target;
  }
  if (condition === "monuments") {
    return ownsAllMonuments({ tiles: source.tiles, owner: "p2" });
  }
  return false;
}

function escortTruckDestroyed(
  state: Pick<GameState, "victoryConditions" | "units" | "initialSupplyTruckCount">,
): boolean {
  if (activeVictoryCondition(state) !== "escort") return false;
  const initial = state.initialSupplyTruckCount ?? 0;
  if (initial <= 0) return false;
  return countPlayerSupplyTrucks(state.units, "p1") < initial;
}

export function victorySubtext({
  status,
  dayNumber,
  state,
}: {
  status: "victory" | "defeat";
  dayNumber: number;
  state: Pick<
    GameState,
    "victoryConditions" | "captureCityTarget" | "tiles" | "units" | "initialSupplyTruckCount"
  >;
}): string {
  const condition = activeVictoryCondition(state);
  if (status === "defeat") {
    if (condition === "capture" && enemyCompletedObjective(state)) {
      return "The enemy captured enough cities.";
    }
    if (condition === "monuments" && enemyCompletedObjective(state)) {
      return "The enemy captured all monuments.";
    }
    if (escortTruckDestroyed(state)) {
      return "The enemy destroyed a Supply Truck.";
    }
    return "Your forces have been wiped out.";
  }
  if (condition === "capture") {
    return `Enough cities captured after ${dayNumber} days.`;
  }
  if (condition === "monuments") {
    return `All monuments captured after ${dayNumber} days.`;
  }
  if (condition === "escort") {
    return `All supply delivered after ${dayNumber} days.`;
  }
  if (condition === "cannon") {
    return `All cannons destroyed after ${dayNumber} days.`;
  }
  return `All enemy units eliminated after ${dayNumber} days.`;
}
